import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboardSaleReport } from "../../../services";
import {
  DashboardSaleReportItem,
  PaginatedDashboardSaleReport,
} from "../../../types/dashboard.type";
import {
  DashboardPeriod,
  PERIOD_NOUN_LABEL,
  PeriodRange,
} from "../../../utils/dashboardPeriod";
import { ChartMetric, METRIC_OPTIONS } from "./metric";
import { formatNumber, numberWithCommas } from "../../../utils";
import Typography from "@mui/material/Typography";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Skeleton from "@mui/material/Skeleton";
import TableBody from "@mui/material/TableBody";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import VisibilityIcon from "@mui/icons-material/Visibility";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PeriodSalesChart from "./PeriodSalesChart";
import ReportPagination from "../../Report/ReportList/Pagination";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

const COLUMN_COUNT = 8;

interface DashboardReportTableProps {
  dateRange: PeriodRange;
  branchCode: string;
  period: DashboardPeriod;
}

const borderRight = (theme: { palette: { draft: { main: string } } }) =>
  `1px solid ${theme.palette.draft.main}`;

const renderSkeletonRows = () =>
  Array.from({ length: 10 }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: COLUMN_COUNT }).map((__, colIndex) => (
        <TableCell key={colIndex}>
          <Skeleton variant="text" />
        </TableCell>
      ))}
    </TableRow>
  ));

function ViewSummaryLink({ onClick }: { onClick: () => void }) {
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.25,
        cursor: "pointer",
        color: "link.main",
        fontWeight: 600,
        flexShrink: 0,
        "&:hover": { textDecoration: "underline" },
      }}
    >
      <Typography variant="body2" fontWeight={600} color="inherit">
        ดูสรุปยอดขาย
      </Typography>
      <KeyboardArrowRightIcon fontSize="small" />
    </Box>
  );
}

export default function DashboardReportTable({
  dateRange,
  branchCode,
  period,
}: DashboardReportTableProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [reports, setReports] = useState<DashboardSaleReportItem[]>([]);
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [metric, setMetric] = useState<ChartMetric>("sales");

  // The period graph only shows for รายเดือน / รายปี (รายวัน = table only).
  const showChart = period !== "today";

  const onGetSaleReport = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const {
        data: { data },
      } = await getDashboardSaleReport(
        dateRange.date_from,
        dateRange.date_to,
        page,
        limit,
        undefined,
        branchCode,
      );
      const payload = data as PaginatedDashboardSaleReport;
      setReports(payload.rows ?? []);
      setCount(payload.count ?? 0);
    } catch {
      setError(true);
      setReports([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [dateRange.date_from, dateRange.date_to, page, limit, branchCode]);

  useEffect(() => {
    onGetSaleReport();
  }, [onGetSaleReport]);

  // Reset to first page when filters that change the result set change.
  useEffect(() => {
    setPage(1);
  }, [dateRange.date_from, dateRange.date_to, branchCode]);

  const goToSummary = () => navigate("/report/sale");

  return (
    <Paper sx={{ padding: 0 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          padding: "16px 16px 0",
          mb: 2,
        }}
      >
        <Typography variant="body1" fontWeight={600}>
          ยอด{PERIOD_NOUN_LABEL[period]}
        </Typography>
        {showChart ? (
          <RadioGroup
            row
            value={metric}
            onChange={(_, value) => setMetric(value as ChartMetric)}
            sx={{
              gap: 1,
              "& .MuiFormControlLabel-root": { mr: 1 },
              "& .MuiFormControlLabel-label": { fontSize: "0.9rem" },
            }}
          >
            {METRIC_OPTIONS.map((option) => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio size="small" />}
                label={option.label}
              />
            ))}
          </RadioGroup>
        ) : (
          <ViewSummaryLink onClick={goToSummary} />
        )}
      </Box>

      {showChart && (
        <Box sx={{ px: 2 }}>
          <PeriodSalesChart
            period={period}
            metric={metric}
            dateRange={dateRange}
            branchCode={branchCode}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 1,
              mb: 1,
            }}
          >
            <ViewSummaryLink onClick={goToSummary} />
          </Box>
          <Divider />
        </Box>
      )}

      <TableContainer>
        <Table>
          <TableHead
            sx={{
              bgcolor: (theme) => theme.palette.background.default,
              "& th": {
                color: (theme) => theme.palette.gray.main,
                fontWeight: 600,
              },
            }}
          >
            <TableRow>
              <TableCell rowSpan={2} align="left" sx={{ borderRight }}>
                สาขา
              </TableCell>
              <TableCell rowSpan={2} align="right" sx={{ borderRight }}>
                ยอดขายก่อน VAT
              </TableCell>
              <TableCell rowSpan={2} align="right" sx={{ borderRight }}>
                ยอดขายหลัง VAT
              </TableCell>
              <TableCell colSpan={2} align="center" sx={{ borderRight }}>
                เงินเชื่อ
              </TableCell>
              <TableCell colSpan={2} align="center" sx={{ borderRight }}>
                เงินสด
              </TableCell>
              <TableCell rowSpan={2} align="center" sx={{ borderRight }}>
                จำนวนบิลยกเลิก
              </TableCell>
              <TableCell rowSpan={2} align="center">
                ดูรายงาน
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell align="right" sx={{ borderRight }}>
                จำนวน
              </TableCell>
              <TableCell align="center" sx={{ borderRight }}>
                %
              </TableCell>
              <TableCell align="right" sx={{ borderRight }}>
                จำนวน
              </TableCell>
              <TableCell align="center" sx={{ borderRight }}>
                %
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && renderSkeletonRows()}

            {!loading && error && (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT + 1}
                  align="center"
                  sx={{ height: "200px", [compact]: { height: "120px" } }}
                >
                  <Typography color="error" fontWeight={500}>
                    ไม่สามารถโหลดข้อมูลได้
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading && !error && reports.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={COLUMN_COUNT + 1}
                  align="center"
                  sx={{ height: "200px", [compact]: { height: "120px" } }}
                >
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูล
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              !error &&
              reports.map((item, index) => (
                <TableRow key={item.sale_report_uuid || index}>
                  <TableCell>{item.branch_name || item.branch_code}</TableCell>
                  <TableCell align="right">
                    {formatNumber(item.total_with_tax)}
                  </TableCell>
                  <TableCell align="right">
                    {formatNumber(item.grand_total)}
                  </TableCell>
                  <TableCell align="right">
                    ฿{formatNumber(item.credit_amount)}
                  </TableCell>
                  <TableCell align="center">
                    {item.credit_amount_percent}%
                  </TableCell>
                  <TableCell align="right">
                    ฿{formatNumber(item.cash_amount)}
                  </TableCell>
                  <TableCell align="center">
                    {item.cash_amount_percent}%
                  </TableCell>
                  <TableCell align="center">
                    {numberWithCommas(item.bill_cancel_count)}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="info"
                      disabled={!item.sale_report_uuid}
                      sx={{ p: 1, gap: 0.5 }}
                      onClick={() =>
                        navigate(
                          `/report/sale/details/${item.sale_report_uuid}`,
                        )
                      }
                    >
                      <VisibilityIcon fontSize="small" />
                      <Typography variant="subtitle2">ดูรายงาน</Typography>
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!error && (
        <Box>
          <ReportPagination
            totalCount={count}
            page={page}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
          />
        </Box>
      )}
    </Paper>
  );
}
